import { useEffect, useState } from 'react';
import { VARIANTS, DEFAULT_VARIANT } from './variants';

// Flags live in the DepthMe app's Supabase project, not necessarily the one
// this site's waitlist uses, so they are read over plain REST with their own
// env pair. The anon policy only exposes enabled `funnel_variant_%` rows, so a
// disabled variant simply never comes back.
const FLAGS_URL =
  import.meta.env?.VITE_DEPTHME_SUPABASE_URL || import.meta.env?.VITE_SUPABASE_URL;
const FLAGS_KEY =
  import.meta.env?.VITE_DEPTHME_SUPABASE_ANON_KEY || import.meta.env?.VITE_SUPABASE_ANON_KEY;
const STORAGE_KEY = 'depthme_funnel_variant';
const TIMEOUT_MS = 3000;

const storage = {
  get() {
    try { return window.localStorage.getItem(STORAGE_KEY); } catch { return null; }
  },
  set(v) {
    try { window.localStorage.setItem(STORAGE_KEY, v); } catch { /* private mode */ }
  },
};

async function fetchEnabledWeights() {
  if (!FLAGS_URL || !FLAGS_KEY) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(
      `${FLAGS_URL}/rest/v1/feature_flags?select=key,rollout_pct&enabled=eq.true&key=like.funnel_variant_*&limit=10`,
      { headers: { apikey: FLAGS_KEY, Authorization: `Bearer ${FLAGS_KEY}` }, signal: ctrl.signal },
    );
    if (!res.ok) return null;
    const rows = await res.json();
    const weights = {};
    for (const row of rows) {
      const letter = row.key.replace('funnel_variant_', '');
      if (VARIANTS[letter]) weights[letter] = Math.max(0, Number(row.rollout_pct) || 0);
    }
    return weights;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function pickWeighted(weights) {
  const entries = Object.entries(weights).filter(([, w]) => w > 0);
  const total = entries.reduce((sum, [, w]) => sum + w, 0);
  if (!total) return null;
  let roll = Math.random() * total;
  for (const [key, w] of entries) {
    roll -= w;
    if (roll < 0) return key;
  }
  return entries[entries.length - 1][0];
}

// URL override > sticky assignment > weighted draw > A. Returns null while
// resolving so the page can hold its skeleton rather than flash variant A.
export function useFunnelVariant(requested) {
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const req = requested?.toLowerCase();
    // A direct link (/mindrest/a … /mindrest/e) always shows that variant, even when
    // it's switched off — the flags only govern the random split on /mindrest.
    if (VARIANTS[req]) {
      setVariant(VARIANTS[req]);
      return undefined;
    }
    fetchEnabledWeights().then((weights) => {
      if (cancelled) return;
      let key = DEFAULT_VARIANT;
      if (weights) {
        const stored = storage.get();
        key = stored && stored in weights ? stored : pickWeighted(weights) || DEFAULT_VARIANT;
      }
      storage.set(key);
      setVariant(VARIANTS[key]);
    });
    return () => { cancelled = true; };
  }, [requested]);

  return variant;
}
